import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Stack,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  DeviceHub as DeviceIcon,
  CheckCircle as CheckCircleIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  PhonelinkSetup as HandheldIcon,
  RadioButtonChecked,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  HelpOutline as UnknownDeviceIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useDeviceSelection } from '../../hooks/useDeviceSelection';
import { useMQTT } from '../../hooks/useMQTT';
import { saveSelectedRfidDevice, getSelectedRfidDevice } from '../../utils/rfidDeviceUtils';
import { getUnknownDevices, updateDeviceLocation, getDeviceById, updateDevice } from '../../services/api';

const Settings = () => {
  const {
    selectedDevices,
    availableDevices,
    selectedDeviceObjects,
    loading,
    error,
    updateSelectedDevices,
    toggleDevice,
    selectAllDevices,
    deselectAllDevices,
    saveDevices,
    isDeviceSelected,
    getDeviceName,
    setError
  } = useDeviceSelection();
  
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [cacheDialogOpen, setCacheDialogOpen] = useState(false);
  
  // Handheld device state
  const [selectedHandheldDevice, setSelectedHandheldDevice] = useState('');
  const [handheldSaveSuccess, setHandheldSaveSuccess] = useState(false);
  
  // Unknown devices state
  const [unknownDevices, setUnknownDevices] = useState([]);
  const [unknownDevicesLoading, setUnknownDevicesLoading] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [savingLocation, setSavingLocation] = useState(false);
  
  // MQTT hook for handheld devices
  const { isConnected } = useMQTT();

  // Load saved handheld device on mount
  useEffect(() => {
    const savedDevice = getSelectedRfidDevice('audit') || '';
    setSelectedHandheldDevice(savedDevice);
  }, []);

  // Load unknown devices on mount
  useEffect(() => {
    fetchUnknownDevices();
  }, []);

  // Fetch unknown devices
  const fetchUnknownDevices = async () => {
    setUnknownDevicesLoading(true);
    try {
      const response = await getUnknownDevices();
      if (response.success && response.data) {
        setUnknownDevices(response.data);
      }
    } catch (error) {
      setError('Failed to load unknown devices');
    } finally {
      setUnknownDevicesLoading(false);
    }
  };

  // Handle device click to open location dialog
  const handleDeviceClick = (deviceId) => {
    setSelectedDeviceId(deviceId);
    setLocationName('');
    setLocationDialogOpen(true);
  };

  // Handle save location
  const handleSaveLocation = async () => {
    if (!selectedDeviceId) return;
    
    setSavingLocation(true);
    try {
      // First, update location name using PATCH
      await updateDeviceLocation(selectedDeviceId, locationName);
      
      // Then, get the updated device data
      const deviceResponse = await getDeviceById(selectedDeviceId);
      
      if (deviceResponse.success && deviceResponse.data) {
        const device = deviceResponse.data;
        
        // Update device using PUT with all fields
        await updateDevice(selectedDeviceId, {
          name: device.name,
          descriptions: device.descriptions || '',
          locationName: locationName
        });
      }
      
      // Close dialog
      setLocationDialogOpen(false);
      setSelectedDeviceId(null);
      setLocationName('');
      setSavingLocation(false);
      
      // Refresh the unknown devices list
      await fetchUnknownDevices();
    } catch (error) {
      setError('Failed to save location name');
      setSavingLocation(false);
    }
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    setLocationDialogOpen(false);
    setSelectedDeviceId(null);
    setLocationName('');
  };

  // Handle handheld device selection change
  const handleHandheldDeviceChange = (deviceId) => {
    setSelectedHandheldDevice(deviceId);
    setHandheldSaveSuccess(false);
  };

  // Save handheld device selection
  const handleSaveHandheldDevice = () => {
    saveSelectedRfidDevice(selectedHandheldDevice, 'audit');
    setHandheldSaveSuccess(true);
    setTimeout(() => {
      setHandheldSaveSuccess(false);
    }, 3000);
  };

  // Handle device selection change
  const handleDeviceChange = (deviceId, isChecked) => {
    toggleDevice(deviceId);
    setSaveSuccess(false);
  };

  // Handle select all devices
  const handleSelectAll = () => {
    selectAllDevices();
    setSaveSuccess(false);
  };

  // Handle deselect all devices
  const handleDeselectAll = () => {
    deselectAllDevices();
    setSaveSuccess(false);
  };

  // Save selected devices to localStorage
  const handleSave = async () => {
    const success = await saveDevices();
    if (success) {
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    }
  };

  // Refresh devices from backend
  const handleRefresh = async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  // Get device name by ID
  const getDeviceNameById = (deviceId) => {
    return getDeviceName(deviceId);
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>

      {/* Header */}
      <Box sx={{ mb: 3, textAlign: { xs: 'center', sm: 'left' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-start' }, gap: 1, mb: 1 }}>
          <SettingsIcon color="primary" sx={{ fontSize: { xs: 28, sm: 32 } }} />
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 600 }}>
            Settings
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Configure devices and system preferences
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {saveSuccess && (
        <Alert 
          severity="success" 
          sx={{ mb: 3, borderRadius: 2 }}
          icon={<CheckCircleIcon />}
        >
          Device preferences saved!
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Device Selection Card */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DeviceIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                    Device List
                  </Typography>
                  <Badge badgeContent={selectedDevices.length} color="primary" />
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Refresh">
                    <IconButton 
                      onClick={handleRefresh} 
                      disabled={loading}
                      size="small"
                      sx={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={loading}
                    size="small"
                    sx={{ borderRadius: 2 }}
                  >
                    Save
                  </Button>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Select devices for browser use
              </Typography>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <>
                  {availableDevices.length === 0 ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      No devices available
                    </Alert>
                  ) : (
                    <>
                      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button 
                          variant="outlined" 
                          size="small" 
                          onClick={handleSelectAll}
                          disabled={selectedDevices.length === availableDevices.length}
                          sx={{ borderRadius: 2, fontSize: '0.75rem' }}
                        >
                          Select All
                        </Button>
                        <Button 
                          variant="outlined" 
                          size="small" 
                          onClick={handleDeselectAll}
                          disabled={selectedDevices.length === 0}
                          sx={{ borderRadius: 2, fontSize: '0.75rem' }}
                        >
                          Clear All
                        </Button>
                      </Box>

                      <FormGroup>
                        {availableDevices.map((device) => (
                          <FormControlLabel
                            key={device.id}
                            control={
                              <Checkbox
                                checked={isDeviceSelected(device.id)}
                                onChange={(e) => handleDeviceChange(device.id, e.target.checked)}
                                color="primary"
                                size="small"
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                  {device.name}
                                </Typography>
                                <Chip 
                                  label={device.id} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              </Box>
                            }
                            sx={{ mb: 0.5 }}
                          />
                        ))}
                      </FormGroup>

                      <Divider sx={{ my: 2 }} />

                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 600 }}>
                          Selected ({selectedDevices.length})
                        </Typography>
                        {selectedDevices.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                            No devices selected
                          </Typography>
                        ) : (
                          <List dense sx={{ maxHeight: 120, overflow: 'auto' }}>
                            {selectedDevices.map((deviceId) => (
                              <ListItem key={deviceId} sx={{ py: 0.25, px: 0 }}>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                                      {getDeviceNameById(deviceId)}
                                    </Typography>
                                  }
                                  secondary={
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                      {deviceId}
                                    </Typography>
                                  }
                                />
                                <ListItemSecondaryAction>
                                  <Chip 
                                    label="✓" 
                                    size="small" 
                                    color="primary" 
                                    variant="filled"
                                    sx={{ fontSize: '0.7rem', height: 18, minWidth: 18 }}
                                  />
                                </ListItemSecondaryAction>
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </Box>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Handheld Device Card */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HandheldIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                    Handheld Device
                  </Typography>
                  {selectedHandheldDevice && (
                    <Chip 
                      label="✓" 
                      size="small" 
                      color="success" 
                      variant="filled"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  )}
                </Box>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveHandheldDevice}
                  disabled={loading || !selectedHandheldDevice}
                  size="small"
                  sx={{ borderRadius: 2 }}
                >
                  Save
                </Button>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Select RFID device for inventory operations
              </Typography>

              {handheldSaveSuccess && (
                <Alert 
                  severity="success" 
                  sx={{ mb: 2, borderRadius: 2 }}
                  icon={<CheckCircleIcon />}
                >
                  Handheld device saved!
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <>
                  {availableDevices.length === 0 ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      No devices available
                    </Alert>
                  ) : (
                    <>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Handheld Device</InputLabel>
                        <Select
                          value={selectedHandheldDevice}
                          onChange={(e) => handleHandheldDeviceChange(e.target.value)}
                          label="Handheld Device"
                          disabled={!isConnected}
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="">
                            <em>Select Device</em>
                          </MenuItem>
                          {availableDevices.map((device) => (
                            <MenuItem key={device.id} value={device.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <RadioButtonChecked sx={{ fontSize: 16, color: 'green' }} />
                                <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                  {device.name}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {selectedHandheldDevice && (
                        <Paper sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                            Selected Device:
                          </Typography>
                          <Stack spacing={0.5}>
                            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                              Device: {getDeviceName(selectedHandheldDevice)} ({selectedHandheldDevice})
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                              MQTT: {isConnected ? '✅ Connected' : '❌ Disconnected'}
                            </Typography>
                          </Stack>
                        </Paper>
                      )}
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Cache Management Card */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StorageIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                    Cache Management
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<SpeedIcon />}
                  onClick={() => setCacheDialogOpen(true)}
                  size="small"
                  sx={{ borderRadius: 2 }}
                >
                  Manage
                </Button>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Optimize performance and storage
              </Typography>

              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 600 }}>
                  Benefits:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ mt: 1, pl: 2, fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                  <li>Faster loading</li>
                  <li>Reduced network usage</li>
                  <li>Offline functionality</li>
                  <li>Persistent settings</li>
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Unknown Devices Card */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <UnknownDeviceIcon color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                    Unknown Devices
                  </Typography>
                  {unknownDevices.length > 0 && (
                    <Badge badgeContent={unknownDevices.length} color="primary" />
                  )}
                </Box>
                <Tooltip title="Refresh">
                  <span>
                    <IconButton 
                      onClick={fetchUnknownDevices} 
                      disabled={unknownDevicesLoading}
                      size="small"
                      sx={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Assign location names to unknown devices
              </Typography>

              {unknownDevicesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <>
                  {unknownDevices.length === 0 ? (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      No unknown devices found
                    </Alert>
                  ) : (
                    <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {unknownDevices.map((device) => (
                        <ListItem
                          key={device._id}
                          onClick={() => handleDeviceClick(device._id)}
                          sx={{
                            cursor: 'pointer',
                            borderRadius: 2,
                            mb: 1,
                            '&:hover': {
                              backgroundColor: 'rgba(59, 130, 246, 0.08)',
                            },
                            border: '1px solid',
                            borderColor: 'divider',
                            py: 1.5,
                            px: 2
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 500 }}>
                                Device ID: {device._id}
                              </Typography>
                            }
                            secondary={
                              <>
                                <Typography variant="caption" component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', mt: 0.5 }}>
                                  Name: {device.name || 'N/A'}
                                </Typography>
                                {device.descriptions && (
                                  <Typography variant="caption" component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', mt: 0.5 }}>
                                    {device.descriptions}
                                  </Typography>
                                )}
                              </>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Chip 
                              label="Click to assign" 
                              size="small" 
                              color="warning" 
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Location Name Dialog */}
      <Dialog 
        open={locationDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
              Assign Location Name
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              Device ID: <strong>{selectedDeviceId}</strong>
            </Typography>
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label="Location Name"
            type="text"
            fullWidth
            variant="outlined"
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
            sx={{ borderRadius: 2 }}
            placeholder="Enter location name (e.g., Warehouse A - Shelf 1)"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            disabled={savingLocation}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveLocation}
            variant="contained"
            startIcon={savingLocation ? <CircularProgress size={16} /> : <SaveIcon />}
            disabled={savingLocation || !locationName.trim()}
            sx={{ borderRadius: 2 }}
          >
            {savingLocation ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;